import { AppealStatus, APPEAL_STATUS_LABEL } from '@repo/types';

import { get } from 'lodash';

/**
 * Full Tailwind class strings per appeal status, written as static literals.
 *
 * These MUST stay literal — do NOT rebuild them via interpolation such as
 * `bg-[${color}]/20`. Tailwind's JIT scans source statically at build time and
 * never sees runtime-interpolated arbitrary values, so those classes generate
 * no CSS. That is why every status except To Be Appealed rendered without a
 * background: only `#3b82f5/20` happened to exist as a literal elsewhere, so
 * only that one had its pill background generated.
 *
 * Colors mirror APPEAL_STATUS_COLOR in @repo/types; keep them in sync.
 */
const APPEAL_STATUS_CLASS: Record<AppealStatus, { light: string; dark: string }> = {
  PRE_APPEAL: {
    light: 'text-[#374151] bg-[#374151]/20 border-[#374151]/40',
    dark: 'text-[#FFFFFF] bg-[#374151] border-[#374151]',
  },
  TO_BE_APPEALED: {
    light: 'text-[#3b82f5] bg-[#3b82f5]/20 border-[#3b82f5]/40',
    dark: 'text-[#FFFFFF] bg-[#3b82f5] border-[#3b82f5]',
  },
  APPEAL_SUBMITTED: {
    light: 'text-[#1E40AF] bg-[#1E40AF]/20 border-[#1E40AF]/40',
    dark: 'text-[#FFFFFF] bg-[#1E40AF] border-[#1E40AF]',
  },
  NO_APPEAL: {
    light: 'text-[#5B21B6] bg-[#5B21B6]/20 border-[#5B21B6]/40',
    dark: 'text-[#FFFFFF] bg-[#5B21B6] border-[#5B21B6]',
  },
  APPEAL_APPROVED: {
    light: 'text-[#065F46] bg-[#065F46]/20 border-[#065F46]/40',
    dark: 'text-[#FFFFFF] bg-[#065F46] border-[#065F46]',
  },
  APPEAL_DENIED: {
    light: 'text-[#991B1B] bg-[#991B1B]/20 border-[#991B1B]/40',
    dark: 'text-[#FFFFFF] bg-[#991B1B] border-[#991B1B]',
  },
  DEADLINE_EXPIRED: {
    light: 'text-[#991B1B] bg-[#991B1B]/20 border-[#991B1B]/40',
    dark: 'text-[#FFFFFF] bg-[#991B1B] border-[#991B1B]',
  },
  CLOSED: {
    light: 'text-[#4B5563] bg-[#4B5563]/20 border-[#4B5563]/40',
    dark: 'text-[#FFFFFF] bg-[#4B5563] border-[#4B5563]',
  },
  AWAITING_APPEAL_SUBMISSION: {
    light: 'text-[#804F00] bg-[#804F00]/20 border-[#804F00]/40',
    dark: 'text-[#FFFFFF] bg-[#804F00] border-[#804F00]',
  },
};

const useAppealStatus = (mode: 'dark' | 'light') => {
  const getAppealStatusLabel = (appealStatus: AppealStatus): string =>
    get(APPEAL_STATUS_LABEL, appealStatus, appealStatus);
  const getAppealStatusColor = (appealStatus: AppealStatus): string => {
    const classes = APPEAL_STATUS_CLASS[appealStatus];
    if (!classes) return 'text-xtnd-dark dark:text-xtnd-light';
    return classes[mode];
  };

  return { getAppealStatusLabel, getAppealStatusColor };
};

export default useAppealStatus;
